import React, { useState, useEffect } from 'react'
import { getFiles, getFileCategories } from '../services/api'

const Downloads = () => {
  const [allFiles, setAllFiles] = useState([])
  const [displayedFiles, setDisplayedFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchActive, setSearchActive] = useState(false) // 是否已执行搜索
  const [panelOpen, setPanelOpen] = useState(false) // 搜索面板是否展开

  // 搜索表单临时状态（面板内的输入值，未确认前不生效）
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [sortBy, setSortBy] = useState('newest')

  const [categories, setCategories] = useState([
    { id: 'all', name: '全部文件', count: 0 }
  ])

  const sortOptions = [
    { id: 'newest', name: '最新更新' },
    { id: 'name', name: '文件名称' },
    { id: 'downloads', name: '下载次数' },
    { id: 'size', name: '文件大小' }
  ]

  // 页面加载时获取所有文件，然后统计分类
  useEffect(() => {
    const init = async () => {
      setLoading(true)
      try {
        const result = await getFiles({ limit: 100 })
        const files = result.files || []
        setAllFiles(files)
        setDisplayedFiles(files)
        setSearchActive(false)
        // 直接从已加载的文件中统计分类数量，避免额外请求
        buildCategoriesFromFiles(files)
      } catch (error) {
        console.error('加载文件失败:', error)
        setAllFiles([])
        setDisplayedFiles([])
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [])

  // 从已加载的文件列表中构建分类统计（不发额外请求）
  const buildCategoriesFromFiles = async (files) => {
    try {
      const categoryList = await getFileCategories()
      const categoriesWithCounts = categoryList.map(category => ({
        id: category,
        name: getCategoryDisplayName(category),
        count: files.filter(f => f.category === category).length
      }))
      setCategories([
        { id: 'all', name: '全部文件', count: files.length },
        ...categoriesWithCounts
      ])
    } catch (error) {
      console.error('加载分类失败:', error)
      // 降级：从 files 中直接统计
      const defaultCats = ['whitepaper', 'manual', 'driver', 'software', 'product', 'document', 'other']
      const catsWithCounts = defaultCats.map(cat => ({
        id: cat,
        name: getCategoryDisplayName(cat),
        count: files.filter(f => f.category === cat).length
      }))
      setCategories([
        { id: 'all', name: '全部文件', count: files.length },
        ...catsWithCounts
      ])
    }
  }

  const getCategoryDisplayName = (category) => {
    const nameMap = {
      whitepaper: '技术白皮书',
      manual: '用户手册',
      driver: '驱动程序',
      software: '软件工具',
      product: '产品资料',
      document: '技术文档',
      other: '其他文件'
    }
    return nameMap[category] || category
  }

  // 执行搜索
  const handleSearch = () => {
    let filtered = [...allFiles]

    // 按搜索词过滤
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(file =>
        (file.displayName || file.name).toLowerCase().includes(term) ||
        (file.description || '').toLowerCase().includes(term)
      )
    }

    // 按分类过滤
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(file => file.category === selectedCategory)
    }

    // 排序
    filtered.sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.updatedAt) - new Date(a.updatedAt)
      } else if (sortBy === 'name') {
        return (a.displayName || a.name).localeCompare(b.displayName || b.name)
      } else if (sortBy === 'downloads') {
        return (b.downloadCount || 0) - (a.downloadCount || 0)
      } else if (sortBy === 'size') {
        const getSizeInMB = (sizeStr) => parseFloat(sizeStr) || 0
        return getSizeInMB(b.fileSize) - getSizeInMB(a.fileSize)
      }
      return 0
    })

    setDisplayedFiles(filtered)
    setSearchActive(true)
  }

  // 重置搜索 / 取消搜索
  const handleReset = () => {
    setSearchTerm('')
    setSelectedCategory('all')
    setSortBy('newest')
    setDisplayedFiles(allFiles)
    setSearchActive(false)
    setPanelOpen(false)
  }

  // 打开搜索面板
  const handleOpenPanel = () => {
    setPanelOpen(true)
  }

  // 关闭搜索面板（不重置搜索条件，但也不执行搜索）
  const handleClosePanel = () => {
    setPanelOpen(false)
  }

  const getFileIcon = (fileType) => {
    const iconMap = {
      pdf: '📄',
      zip: '📦',
      exe: '⚙️',
      doc: '📝',
      xls: '📊',
      ppt: '📽️'
    }
    return iconMap[fileType] || '📁'
  }

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId)
    return category ? category.name : ''
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-CN')
  }

  const handleDownload = async (file) => {
    const token = localStorage.getItem('token')
    if (file.requiresLogin && !token) {
      alert('此文件需要登录后才能下载，请先登录系统。')
      return
    }

    try {
      const downloadUrl = `/api/downloads/${file.id}/download`
      const headers = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(downloadUrl, { headers })

      if (!response.ok) {
        if (response.status === 401) {
          alert('此文件需要登录后才能下载，请先登录系统。')
          return
        }
        throw new Error(`下载失败: ${response.status}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.displayName || file.name
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      loadAllFiles()
    } catch (error) {
      console.error('下载文件失败:', error)
      alert('下载文件失败，请稍后重试')
    }
  }

  return (
    <div className="pt-20 min-h-screen bg-gray-50 relative overflow-hidden">
      <div className="container-max section-padding">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-tech mb-4">
            技术支持
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            获取我们的技术文档、驱动程序、用户手册等资源文件
          </p>
        </div>

        {/* Main content area */}
        <div>
          {/* Search Toggle Button - floated to right, positioned above first file */}
          <div className="relative mb-2">
            <button
              onClick={handleOpenPanel}
              className={`absolute right-0 -top-1 flex items-center gap-2 px-4 py-2 rounded-lg shadow-md transition-all duration-300 ${
                panelOpen
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary-dark text-white hover:shadow-lg'
              }`}
              disabled={panelOpen}
              title="展开搜索面板"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-sm">搜索文件</span>
            </button>
          </div>

          {/* Results Count & Active Filters */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-gray-600">
              {searchActive ? (
                <span>
                  找到 <span className="font-semibold text-primary">{displayedFiles.length}</span> 个文件
                  {searchTerm && (
                    <span className="ml-2 text-sm text-gray-400">
                      （搜索: "{searchTerm}"）
                    </span>
                  )}
                  {selectedCategory !== 'all' && (
                    <span className="ml-2 text-sm text-gray-400">
                      （分类: {getCategoryName(selectedCategory)}）
                    </span>
                  )}
                </span>
              ) : (
                <span>
                  共 <span className="font-semibold text-primary">{displayedFiles.length}</span> 个可下载文件
                </span>
              )}
            </p>
            {searchActive && (
              <button
                onClick={handleReset}
                className="text-sm text-primary hover:text-primary-dark underline transition-colors"
              >
                清除筛选，显示全部文件
              </button>
            )}
          </div>

          {/* Files List */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : displayedFiles.length > 0 ? (
            <div className="space-y-4">
              {displayedFiles.map(file => (
                <div key={file.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="text-3xl">
                          {getFileIcon(file.fileType)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-tech">
                              {file.displayName || file.name}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              file.requiresLogin
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {file.requiresLogin ? '需登录' : '公开'}
                            </span>
                          </div>
                          <p className="text-gray-600 mb-3">
                            {file.description}
                          </p>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <span className="bg-gray-100 px-2 py-1 rounded">
                              {getCategoryName(file.category)}
                            </span>
                            <span>大小: {file.fileSize}</span>
                            <span>下载: {file.downloadCount} 次</span>
                            <span>更新: {formatDate(file.updatedAt)}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(file)}
                        className="btn-primary whitespace-nowrap ml-4"
                      >
                        下载文件
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-tech mb-2">未找到相关文件</h3>
              <p className="text-gray-600 mb-4">请尝试调整搜索条件或筛选条件</p>
              <button
                onClick={handleReset}
                className="text-primary hover:text-primary-dark underline"
              >
                查看全部文件
              </button>
            </div>
          )}

          {/* Slide-out Search Panel - from right side */}
          {/* Overlay */}
          <div
            className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
              panelOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
            onClick={handleClosePanel}
          />

          {/* Panel */}
          <div
            className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
              panelOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex flex-col h-full">
              {/* Panel Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-tech flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  搜索与筛选
                </h2>
                <button
                  onClick={handleClosePanel}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Panel Body - Search Form */}
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                {/* Search Input */}
                <div>
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                    搜索文件
                  </label>
                  <input
                    type="text"
                    id="search"
                    placeholder="输入文件名称或描述..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearch()
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>

                {/* Category Filter */}
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                    文件分类
                  </label>
                  <select
                    id="category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name} ({category.count})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sort */}
                <div>
                  <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-2">
                    排序方式
                  </label>
                  <select
                    id="sort"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                  >
                    {sortOptions.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quick Category Pills */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    快速筛选
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          selectedCategory === cat.id
                            ? 'bg-primary text-white shadow-md'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Panel Footer - Action Buttons */}
              <div className="border-t border-gray-200 px-6 py-5 space-y-3">
                <button
                  onClick={handleSearch}
                  className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  确认搜索
                </button>
                <button
                  onClick={handleReset}
                  className="w-full py-3 text-base border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  重置并显示全部
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Download Notice */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-blue-900 mb-2">下载说明</h4>
              <ul className="text-blue-800 space-y-1 text-sm">
                <li>• 公开文件可直接下载，无需登录</li>
                <li>• 标注"需登录"的文件需要登录系统后才能下载</li>
                <li>• 如有下载问题，请联系技术支持</li>
                <li>• 文件仅供学习和参考使用，请勿用于商业用途</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Downloads
